<script>
import tcLib from "@cclib/lib/timecode.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    canUndoAgentTurn,
    resolveAgentConfirmation,
    reviewAgentActivity,
    undoAgentTurn,
} from "@app/store/assistantStore.svelte.js";
import * as m from "@app/paraglide/messages.js";
import { wordDiff } from "@app/lib/assistant/wordDiff.js";

const MAX_DIFF_ROWS = 20;
const WRITE_ICONS = {
    delete_events: "bi-trash",
    create_events: "bi-plus-square",
    set_group_settings: "bi-sliders",
    create_group: "bi-folder-plus",
};

let { activity, turnId } = $props();

let showDiff = $state(false);

let isWrite = $derived(Boolean(activity.counts));
let awaiting = $derived(activity.status === "awaiting_confirmation");
let busy = $derived(activity.status === "pending" || activity.status === "running");
let rows = $derived((activity.changes ?? []).slice(0, MAX_DIFF_ROWS));
let hiddenRows = $derived(Math.max(0, (activity.touched ?? 0) - rows.length));
let undoable = $derived(canUndoAgentTurn(turnId));

function toolLabel(name) {
    return String(name ?? "").replace(/_/g, " ");
}

function formatTime(seconds) {
    return tcLib.secToTc(seconds + (projectState.incode || 0), projectState.frameRate, projectState.dropFrame);
}

function writeLabel(a) {
    const group = a.groupName ?? "";
    const { updated = 0, created = 0, deleted = 0 } = a.counts ?? {};
    if (a.kind === "create_group") return m.assistant_activity_created_group({ count: created, group });
    if (a.touched === 0 && a.settings?.length) return m.assistant_activity_settings({ group });
    if (deleted && !updated && !created) return m.assistant_activity_deleted({ count: deleted, group });
    if (created && !updated && !deleted) return m.assistant_activity_created({ count: created, group });
    return m.assistant_activity_edited({ count: a.touched, group });
}

function confirmLabel(a) {
    const group = a.groupName ?? "";
    if (a.name === "delete_events") return m.assistant_confirm_delete({ count: a.counts?.deleted ?? a.touched, group });
    return m.assistant_confirm_bulk({ count: a.touched, group });
}

function label(a) {
    const group = a.groupName ?? "";
    const tool = toolLabel(a.name);
    switch (a.status) {
        case "pending":
        case "running":
            return m.assistant_activity_running({ tool });
        case "error":
            return m.assistant_activity_failed({ tool });
        case "cancelled":
            return m.assistant_activity_cancelled({ tool });
        case "declined":
            return m.assistant_activity_declined();
        case "noop":
            return m.assistant_activity_noop({ group });
        case "awaiting_confirmation":
            return confirmLabel(a);
    }
    if (isWrite) return writeLabel(a);
    switch (a.kind) {
        case "read_project":
            return m.assistant_activity_read_project();
        case "read_events":
            return m.assistant_activity_read_events({ count: a.count, group });
        case "search":
            return m.assistant_activity_search({ count: a.count, group });
        case "qc":
            return m.assistant_activity_qc({ count: a.count, group });
        case "seek":
            return m.assistant_activity_seek({ time: formatTime(a.time) });
        case "select_group":
            return m.assistant_activity_select_group({ group });
        case "select_events":
            return m.assistant_activity_select_events({ count: a.count, group });
        default:
            return tool;
    }
}

function icon(a) {
    if (busy) return "";
    if (a.status === "error") return "bi-exclamation-triangle text-danger";
    if (a.status === "declined" || a.status === "cancelled") return "bi-x-circle text-muted";
    if (awaiting) return "bi-question-circle text-warning";
    if (isWrite) return `${WRITE_ICONS[a.name] ?? "bi-pencil-square"} text-primary`;
    return "bi-eye text-muted";
}

function timing(snapshot) {
    const tc = (seconds) => (Number.isFinite(seconds) ? formatTime(seconds) : "–");
    return `${tc(snapshot?.start)} – ${tc(snapshot?.end)}`;
}

/** Only the side of the timing that moved: "Out 00:00:19:17 → 00:00:19:12". */
function timingChangeLabel(change) {
    const tc = (seconds) => (Number.isFinite(seconds) ? formatTime(seconds) : "–");
    const { before, after } = change;
    const parts = [];
    if (before.start !== after.start) parts.push(`${m.assistant_activity_time_in()} ${tc(before.start)} → ${tc(after.start)}`);
    if (before.end !== after.end) parts.push(`${m.assistant_activity_time_out()} ${tc(before.end)} → ${tc(after.end)}`);
    return parts.join(" · ");
}

const diffCache = new Map();
/** Word-level diff of an updated event's text (cached per change). */
function wordChanges(change) {
    const key = `${change.id}\u0000${change.before?.text}\u0000${change.after?.text}`;
    if (!diffCache.has(key)) diffCache.set(key, wordDiff(change.before?.text ?? "", change.after?.text ?? ""));
    return diffCache.get(key);
}

/** Accent colour of the card: done writes, approvals, errors, cancelled, reads. */
function statusTone(a) {
    if (a.status === "awaiting_confirmation") return "awaiting";
    if (a.status === "error") return "error";
    if (a.status === "declined" || a.status === "cancelled" || a.status === "noop") return "muted";
    return isWrite ? "done" : "read";
}

function timingChanged(change) {
    return change.before && change.after && (change.before.start !== change.after.start || change.before.end !== change.after.end);
}

function toggleReview() {
    showDiff = !showDiff;
    if (showDiff) reviewAgentActivity(activity);
}
</script>

<div class={["assistant-activity small mb-1", `status-${statusTone(activity)}`]}>
    <div class="d-flex align-items-center gap-2">
        {#if busy}
            <span class="spinner-border spinner-border-sm text-muted flex-shrink-0" aria-hidden="true"></span>
        {:else}
            <i class={["bi flex-shrink-0", icon(activity)]} aria-hidden="true"></i>
        {/if}
        <span class="assistant-activity-label flex-grow-1 text-truncate" title={activity.error ?? label(activity)}>{label(activity)}</span>
        {#if isWrite && activity.status === "done"}
            <button type="button" class="assistant-ghost-btn" aria-expanded={showDiff} onclick={toggleReview}>
                <i class={["bi", showDiff ? "bi-eye-slash" : "bi-eye"]} aria-hidden="true"></i>
                {showDiff ? m.assistant_activity_hide() : m.assistant_activity_review()}
            </button>
            <span title={undoable ? m.assistant_activity_undo_title() : m.assistant_activity_undo_unavailable()}>
                <button type="button" class="assistant-ghost-btn" disabled={!undoable} onclick={() => undoAgentTurn(turnId)}>
                    <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                    {m.assistant_activity_undo()}
                </button>
            </span>
        {/if}
    </div>

    {#if (showDiff || awaiting) && rows.length}
        <ul class="list-unstyled mb-0 mt-2 assistant-diff">
            {#each rows as change (change.type + change.id)}
                <li class={["assistant-diff-row", `diff-${change.type}`]}>
                    <div class="d-flex align-items-center gap-2 mb-1">
                        <span class="assistant-diff-number">#{change.number}</span>
                        {#if change.type === "updated" && wordChanges(change).breaksOnly}
                            <span class="assistant-diff-chip">{m.assistant_activity_line_breaks()}</span>
                        {/if}
                        {#if change.type === "created" || change.type === "deleted"}
                            <span class="assistant-diff-chip">{change.type === "created" ? m.assistant_activity_new_event() : m.assistant_activity_removed_event()}</span>
                        {/if}
                        {#if change.type !== "updated" || timingChanged(change)}
                            <span class={["assistant-diff-time ms-auto", timingChanged(change) && "changed"]}>
                                {#if timingChanged(change)}
                                    {timingChangeLabel(change)}
                                {:else}
                                    {timing(change.after ?? change.before)}
                                {/if}
                            </span>
                        {/if}
                    </div>
                    {#if change.type === "deleted"}
                        <div class="assistant-diff-text removed-whole">{change.before.text || "∅"}</div>
                    {:else if change.type === "created"}
                        <div class="assistant-diff-text">{change.after.text || "∅"}</div>
                    {:else if change.before.text !== change.after.text}
                        <div class="assistant-diff-text" aria-label={m.assistant_activity_after()}>
                            {#each wordChanges(change).segments as segment, i (i)}
                                {#if segment.type === "added"}
                                    <ins class="diff-added">{segment.text}</ins>
                                {:else if segment.type === "removed"}
                                    <del class="diff-removed">{segment.text}</del>
                                {:else}
                                    <span>{segment.text}</span>
                                {/if}
                            {/each}
                        </div>
                    {/if}
                </li>
            {/each}
        </ul>
        {#if hiddenRows > 0}
            <div class="text-muted mt-1">{m.assistant_activity_more_changes({ count: hiddenRows })}</div>
        {/if}
    {/if}

    {#if awaiting}
        <div class="d-flex gap-2 justify-content-end mt-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => resolveAgentConfirmation(activity.toolUseId, false)}>
                {m.assistant_confirm_cancel()}
            </button>
            <button type="button" class="btn btn-sm btn-primary" onclick={() => resolveAgentConfirmation(activity.toolUseId, true)}>
                <i class="bi bi-check2 me-1" aria-hidden="true"></i>{m.assistant_confirm_approve()}
            </button>
        </div>
    {/if}
</div>

<style>
    .assistant-activity {
        --accent: var(--bs-primary);
        background-color: var(--bs-tertiary-bg);
        border: 1px solid var(--bs-border-color-translucent);
        border-left: 3px solid var(--accent);
        border-radius: var(--bs-border-radius);
        padding: 0.4rem 0.6rem;
    }

    .assistant-activity.status-awaiting {
        --accent: var(--bs-warning);
        background-color: var(--bs-warning-bg-subtle);
        border-color: var(--bs-warning-border-subtle);
        border-left-color: var(--accent);
    }

    .assistant-activity.status-error {
        --accent: var(--bs-danger);
    }

    .assistant-activity.status-muted {
        --accent: var(--bs-secondary-color);
    }

    .assistant-activity.status-read {
        --accent: var(--bs-border-color);
    }

    .assistant-activity-label {
        font-weight: 500;
    }

    .assistant-ghost-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.1rem 0.4rem;
        border: 0;
        border-radius: var(--bs-border-radius-sm);
        background: transparent;
        /* Emphasis teal: readable on both themes (the bright link teal is not on light backgrounds). */
        color: var(--bs-primary-text-emphasis);
        font-size: 0.8rem;
        line-height: 1.4;
    }

    .assistant-ghost-btn:hover:not(:disabled),
    .assistant-ghost-btn:focus-visible {
        background-color: rgba(var(--bs-primary-rgb), 0.15);
        color: var(--bs-primary-text-emphasis);
        outline: none;
    }

    .assistant-ghost-btn:disabled {
        color: var(--bs-secondary-color);
        opacity: 0.6;
    }

    .assistant-diff {
        max-height: 18rem;
        overflow-y: auto;
        border-top: 1px solid var(--bs-border-color-translucent);
    }

    .assistant-diff-row {
        padding: 0.45rem 0 0.45rem 0.5rem;
        border-bottom: 1px solid var(--bs-border-color-translucent);
        border-left: 2px solid transparent;
    }

    .assistant-diff-row:last-child {
        border-bottom: 0;
    }

    .assistant-diff-row.diff-created {
        border-left-color: var(--bs-primary);
    }

    .assistant-diff-row.diff-deleted {
        border-left-color: var(--bs-danger);
    }

    .assistant-diff-number {
        padding: 0 0.35rem;
        border-radius: var(--bs-border-radius-sm);
        background-color: var(--bs-secondary-bg);
        color: var(--bs-secondary-color);
        font-size: 0.75rem;
        font-variant-numeric: tabular-nums;
    }

    .assistant-diff-chip {
        padding: 0 0.4rem;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: 999px;
        color: var(--bs-secondary-color);
        font-size: 0.72rem;
    }

    .assistant-diff-time {
        min-width: 0;
        color: var(--bs-secondary-color);
        font-size: 0.75rem;
        font-variant-numeric: tabular-nums;
        text-align: right;
    }

    .assistant-diff-time.changed {
        padding: 0 0.4rem;
        border-radius: 999px;
        background-color: rgba(var(--bs-warning-rgb), 0.15);
        color: var(--bs-warning-text-emphasis);
    }

    .assistant-diff-text {
        color: var(--bs-body-color);
        font-size: 0.85rem;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
    }

    .assistant-diff-text.removed-whole {
        color: var(--bs-secondary-color);
        text-decoration: line-through;
    }

    /* Word-level highlights: tinted backgrounds, never coloured text alone. */
    .diff-added {
        padding: 0 0.1rem;
        border-radius: 0.2rem;
        background-color: rgba(var(--bs-primary-rgb), 0.22);
        color: var(--bs-primary-text-emphasis);
        text-decoration: none;
    }

    .diff-removed {
        padding: 0 0.1rem;
        border-radius: 0.2rem;
        background-color: rgba(var(--bs-danger-rgb), 0.18);
        color: var(--bs-danger-text-emphasis);
        text-decoration: line-through;
        text-decoration-color: rgba(var(--bs-danger-rgb), 0.7);
    }
</style>
